import { CaseInfoPanel } from "@frontend/pages/case-workspace/components/case-info-panel"
import { moduleTypeIcon } from "@frontend/pages/case-workspace/radiology/lib/module-schema"
import type { ModuleListBarProps } from "@frontend/pages/case-workspace/radiology/lib/panel-types"
import type { CaseSeries } from "@frontend/pages/case-workspace/radiology/types"
import { Check, ChevronLeft, ChevronRight, Lock } from "lucide-react"
import type React from "react"

export function PullTab({ direction, onClick }: { direction: "left" | "right"; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="h-full w-full flex items-center justify-center hover:bg-accent/40 transition-colors"
      title={direction === "right" ? "Expand series panel" : "Expand module panel"}
    >
      {direction === "right" ? (
        <ChevronRight className="size-3.5 text-muted-foreground" />
      ) : (
        <ChevronLeft className="size-3.5 text-muted-foreground" />
      )}
    </button>
  )
}

interface LeftSeriesPanelProps {
  series: CaseSeries[]
  activeSeriesId: string | undefined
  projectName?: string | null
  caseName?: string | null
  studyDate?: string | null
  onSelectSeries: (id: string) => void
  onDragStart: (event: React.DragEvent, id: string) => void
  onDragEnd: () => void
}

export function LeftSeriesPanel({
  series,
  activeSeriesId,
  projectName,
  caseName,
  studyDate,
  onSelectSeries,
  onDragStart,
  onDragEnd
}: LeftSeriesPanelProps) {
  return (
    <>
      <CaseInfoPanel projectName={projectName} caseName={caseName} studyDate={studyDate} />
      <div className="px-2.5 py-2 text-[10px] font-medium text-muted-foreground uppercase tracking-wide border-b border-border shrink-0">
        Series
      </div>
      <div className="flex-1 overflow-auto p-2 space-y-1.5">
        {series.map((s) => (
          <button
            key={s.id}
            onClick={() => onSelectSeries(s.id)}
            draggable
            onDragStart={(event) => onDragStart(event, s.id)}
            onDragEnd={onDragEnd}
            className={`w-full text-left rounded-md border px-2 py-2 transition-colors ${
              activeSeriesId === s.id
                ? "bg-primary/10 border-primary/40"
                : "bg-surface-raised border-border hover:bg-accent/40"
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-[12px] font-medium truncate">{s.series_description ?? s.series_identifier}</span>
              <span className="text-[10px] text-info uppercase">{s.modality ?? "N/A"}</span>
            </div>
            <div className="text-[10px] text-muted-foreground mt-0.5">
              {s.number_of_frames ?? 0} slices
              {s.voxel_size_z ? ` · ${s.voxel_size_z.toFixed(1)}mm` : ""}
            </div>
          </button>
        ))}
      </div>
    </>
  )
}

export function ModuleListBar({
  modules,
  activeModule,
  onSelectModule,
  moduleCompletions,
  moduleLockById
}: ModuleListBarProps) {
  return (
    <div className="border-b border-border shrink-0 overflow-auto max-h-[40%]">
      {modules.map((mod) => {
        const isComplete = moduleCompletions.find((c) => c.projectModuleId === mod.id)?.completed ?? false
        const lock = moduleLockById.get(mod.id)
        const isLocked = lock?.isEditable === false
        return (
          <button
            key={mod.id}
            onClick={() => onSelectModule(mod.id)}
            title={isLocked ? (lock.lockReason ?? "Module is locked") : undefined}
            className={`w-full text-left px-3 py-2 flex items-center gap-2 transition-colors border-b border-border/50 last:border-b-0 ${
              activeModule?.id === mod.id ? "bg-primary/10" : "hover:bg-accent/40"
            }`}
          >
            {moduleTypeIcon(mod.template_type)}
            <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
              <span className="truncate text-[11px] font-medium">{mod.template_name}</span>
            </div>
            {isLocked && <Lock className="size-3 text-muted-foreground shrink-0" />}
            {isComplete && <Check className="size-3 text-green-500 shrink-0" />}
          </button>
        )
      })}
    </div>
  )
}
